import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Calendar, User, ArrowRight, Eye } from 'lucide-react';
import PhotoGallery from '../PhotoGallery';
import { OptimizedImage } from '../ui/optimized-image';

const NewsManagement = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const token = localStorage.getItem('memberToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/news`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setNews(data);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des news:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getExcerpt = (content) => {
    // Extraire le texte du HTML
    const div = document.createElement('div');
    div.innerHTML = content;
    const text = div.textContent || div.innerText || '';
    
    // Retourner les 200 premiers caractères
    return text.length > 200 ? text.substring(0, 200) + '...' : text;
  };

  const handleReadMore = (article) => {
    setSelectedArticle(article);
  };

  const handleBackToList = () => {
    setSelectedArticle(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" 
             style={{ borderBottomColor: 'rgb(196, 150, 96)' }}></div>
      </div>
    );
  }

  // Vue détail d'un article
  if (selectedArticle) {
    return (
      <div className="space-y-6">
        <Button
          variant="outline"
          onClick={handleBackToList}
          className="mb-4"
          style={{ borderColor: 'rgb(196, 150, 96)', color: 'rgb(196, 150, 96)' }}
        >
          ← Retour aux actualités
        </Button>

        <Card>
          <CardHeader>
            <CardTitle 
              className="text-3xl"
              style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
            >
              {selectedArticle.title}
            </CardTitle>
            <div className="flex items-center gap-4 text-gray-500 text-sm mt-2">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-1" />
                {selectedArticle.author}
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                {formatDate(selectedArticle.published_at)}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {selectedArticle.cover_image && (
              <OptimizedImage 
                src={selectedArticle.cover_image} 
                alt={selectedArticle.title}
                className="w-full h-64 object-cover rounded-lg mb-6"
                priority={true}
                placeholder="blur"
              />
            )}
            <div 
              className="prose prose-lg max-w-none"
              style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
              dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
            />

            {/* Album Photo */}
            {selectedArticle.photo_album && selectedArticle.photo_album.length > 0 && (
              <PhotoGallery 
                photos={selectedArticle.photo_album} 
                columns={3}
              />
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Vue liste des articles
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 
          className="text-3xl font-bold"
          style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
        >
          Actualités & Blog
        </h2>
      </div>

      {news.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500" style={{ fontFamily: '"Lora", serif' }}>
              Aucune actualité publiée pour le moment.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {news.map((article) => (
            <Card key={article.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex gap-6">
                  {article.cover_image && (
                    <OptimizedImage 
                      src={article.cover_image} 
                      alt={article.title}
                      className="w-48 h-32 object-cover rounded-lg flex-shrink-0"
                      useThumbnail={true}
                      placeholder="skeleton"
                    />
                  )}
                  
                  <div className="flex-1">
                    <h3 
                      className="text-2xl font-bold mb-2"
                      style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
                    >
                      {article.title}
                    </h3>
                    
                    <div className="flex items-center gap-4 text-gray-500 text-sm mb-3">
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        {article.author}
                      </div>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(article.published_at)}
                      </div>
                    </div>
                    
                    <p 
                      className="text-gray-600 mb-4"
                      style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                    >
                      {getExcerpt(article.content)}
                    </p>
                    
                    <Button
                      variant="outline"
                      onClick={() => handleReadMore(article)}
                      style={{ 
                        borderColor: 'rgb(196, 150, 96)', 
                        color: 'rgb(196, 150, 96)',
                        fontFamily: '"Lora", serif'
                      }}
                    >
                      Lire la suite
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewsManagement;
